import { useCallback, useEffect, useState } from "react";

/** Detect iOS Safari (iPhone/iPad) — only place that requires the
 *  "Add to Home Screen" manual instructions because Safari doesn't fire
 *  beforeinstallprompt. iPadOS 13+ pretends to be Mac, so we also look
 *  at touch + Apple vendor. */
const detectIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSLegacy = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSLegacy || iPadOS;
};

/** Which browser is running on iOS? Non-Safari iOS browsers can also add
 *  the app to home screen via their own share sheets — the instructions
 *  differ slightly per browser, so we detect and show tailored steps. */
const detectIOSBrowser = () => {
  if (typeof navigator === "undefined") return "safari";
  const ua = navigator.userAgent || "";
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS/.test(ua)) return "firefox";
  if (/EdgiOS/.test(ua)) return "edge";
  if (/OPiOS|OPT\//.test(ua)) return "opera";
  return "safari";
};

const detectStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    // iOS Safari sets navigator.standalone when launched from home screen
    window.navigator.standalone === true
  );
};

const STORAGE_KEY = "lx:pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

/** Module-level singleton — beforeinstallprompt fires exactly once per page
 *  load, so we capture it as soon as the script runs and share the deferred
 *  event with every InstallAppButton instance (nav strip, menu, pill). */
const store = {
  deferredPrompt: null,
  installed: typeof window !== "undefined" ? detectStandalone() : false,
  listeners: new Set(),
};

const emit = () => store.listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    store.deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    store.deferredPrompt = null;
    store.installed = true;
    emit();
  });
  const mql = window.matchMedia?.("(display-mode: standalone)");
  mql?.addEventListener?.("change", () => {
    store.installed = detectStandalone();
    emit();
  });
}

/** Hook returning everything the UI needs to render an Install button. */
export function usePwaInstall() {
  const [, force] = useState(0);
  const [isIOS] = useState(detectIOS);
  const [iosBrowser] = useState(detectIOSBrowser);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    store.listeners.add(fn);
    return () => {
      store.listeners.delete(fn);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const e = store.deferredPrompt;
    if (!e) return { outcome: "unavailable" };
    try {
      e.prompt();
      const choice = await e.userChoice;
      store.deferredPrompt = null;
      if (choice?.outcome === "accepted") store.installed = true;
      emit();
      return choice;
    } catch (_e) {
      return { outcome: "error" };
    }
  }, []);

  const dismissedRecently = (() => {
    try {
      const t = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (!t) return false;
      return Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  })();

  const markDismissed = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  return {
    canInstall: !!store.deferredPrompt,
    isIOS,
    iosBrowser, // 'safari' | 'chrome' | 'firefox' | 'edge' | 'opera'
    isStandalone: store.installed,
    dismissedRecently,
    promptInstall,
    markDismissed,
  };
}
